import React, { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight, ArrowDown, ArrowUpCircle, FileText, Loader2 } from "lucide-react";
import { getBillingTransactions } from "../../extractorApi";
import { SX } from "../studioTokens";
import { engineLabel } from "../studioConst";
import { TX_KINDS, txKindLabel, fmtNum, fmtDateTime, billingErrMsg } from "./billingConst";

const PAGE_SIZE = 10;

/* Ledger rows carry an untyped `meta` blob whose keys depend on the kind —
   summarise only the ones an extraction row actually sets. */
const metaSummary = (meta) => {
  if (!meta || typeof meta !== "object") return null;
  const bits = [];
  if (meta.pages) bits.push(`${meta.pages} page${Number(meta.pages) === 1 ? "" : "s"}`);
  if (meta.engine) bits.push(engineLabel(meta.engine));
  if (meta.model) bits.push(String(meta.model).replace(/_/g, " "));
  return bits.length ? bits.join(" · ") : null;
};

/* Credit ledger. Kind filtering and paging are server-side (the endpoint caps
   limit at 100); the search box only narrows the page already on screen, and
   says so, rather than pretending to search the whole history. */
const TransactionHistory = () => {
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ transactions: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBillingTransactions({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, kind: kind || undefined });
      setData({ transactions: res?.transactions || [], total: Number(res?.total || 0) });
    } catch (err) {
      setError(billingErrMsg(err, "Couldn't load your credit history."));
      setData({ transactions: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [kind, page]);

  useEffect(() => { load(); }, [load]);

  const rows = query.trim()
    ? data.transactions.filter((t) => {
        const q = query.toLowerCase();
        return (t.reference || "").toLowerCase().includes(q) || txKindLabel(t.kind).toLowerCase().includes(q);
      })
    : data.transactions;

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const first = data.total ? page * PAGE_SIZE + 1 : 0;
  const last = Math.min(data.total, page * PAGE_SIZE + data.transactions.length);

  return (
    <div className={SX.card}>
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 pt-4 pb-3.5">
        <select
          value={kind}
          onChange={(e) => { setKind(e.target.value); setPage(0); }}
          className={`${SX.input} cursor-pointer !w-auto min-w-[170px] !py-2`}
        >
          {TX_KINDS.map((k) => <option key={k.value || "all"} value={k.value}>{k.label}</option>)}
        </select>

        <div className="flex items-center gap-2 px-3 py-2 border border-[#ECE9EE] rounded-[9px] bg-white w-[210px] transition-colors focus-within:border-[#A04A97] focus-within:shadow-[0_0_0_3px_rgba(160,74,151,0.14)]">
          <Search size={14} className="text-[#8A8A90] flex-shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this page…"
            className="border-none outline-none text-[12.5px] text-[#1A1A1A] placeholder:text-[#8A8A90] w-full bg-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-14 text-[13px] text-[#8A8A90]">
          <Loader2 size={15} className="animate-spin" /> Loading credit history…
        </div>
      ) : error ? (
        <div className="py-12 px-6 text-center">
          <div className="text-[13px] text-[#B33A1E]">{error}</div>
          <button className={`${SX.btn} ${SX.btnSm} ${SX.btnGhost} mt-3`} onClick={load}>Try again</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 px-6 text-center">
          <FileText size={22} className="text-[#8A8A90] mx-auto mb-2" />
          <div className="text-[13px] text-[#52525B]">
            {data.total === 0
              ? "No credit activity yet. Extractions, grants and purchases will show up here."
              : "Nothing on this page matches your search."}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-[#F5F5F5]">
                {["Date", "Activity", "Type", "Credits", "Balance after"].map((h, i) => (
                  <th
                    key={h}
                    className={`px-5 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[#8A8A90] whitespace-nowrap ${
                      i >= 3 ? "text-right" : "text-left"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const credits = Number(t.credits || 0);
                const positive = credits > 0;
                const meta = metaSummary(t.meta);
                return (
                  <tr key={t.id} className="border-t border-[#ECE9EE] hover:bg-[#FBF5FA] transition-colors">
                    <td className="px-5 py-3 text-[#52525B] whitespace-nowrap align-top">{fmtDateTime(t.created_at)}</td>
                    <td className="px-5 py-3 align-top">
                      <div className="flex items-start gap-2.5">
                        {positive
                          ? <ArrowUpCircle size={15} className="text-[#2F7D44] flex-shrink-0 mt-[2px]" />
                          : <ArrowDown size={15} className="text-[#8A8A90] flex-shrink-0 mt-[2px]" />}
                        <div className="min-w-0">
                          <div className="text-[#1A1A1A] font-medium truncate max-w-[320px]">
                            {t.reference || txKindLabel(t.kind)}
                          </div>
                          {meta && <div className="text-[11.5px] text-[#8A8A90] mt-0.5 truncate max-w-[320px]">{meta}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <span className={`${SX.badge} bg-[#F3F3F3] text-[#6B6B6B]`}>{txKindLabel(t.kind)}</span>
                    </td>
                    <td className={`px-5 py-3 text-right font-semibold whitespace-nowrap align-top tabular-nums ${
                      positive ? "text-[#2F7D44]" : "text-[#1A1A1A]"
                    }`}>
                      {positive ? "+" : ""}{fmtNum(credits)}
                    </td>
                    <td className="px-5 py-3 text-right text-[#52525B] whitespace-nowrap align-top tabular-nums">
                      {t.balance_after == null ? "—" : fmtNum(t.balance_after)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-[#ECE9EE]">
        <span className="text-[12px] text-[#8A8A90]">
          {data.total ? `${first}–${last} of ${fmtNum(data.total)}` : "0 entries"}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#52525B]">Page {page + 1} of {pages}</span>
          <button
            className="w-[30px] h-[30px] rounded-[9px] border border-[#ECE9EE] bg-white text-[#52525B] flex items-center justify-center hover:enabled:bg-[#F5F5F5] disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={loading || page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            className="w-[30px] h-[30px] rounded-[9px] border border-[#ECE9EE] bg-white text-[#52525B] flex items-center justify-center hover:enabled:bg-[#F5F5F5] disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={loading || page >= pages - 1}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
